import { formatCount, formatPercent, formatPeriod, Granularity } from "../reportFormat";
import { DayWindow, formatDays } from "../scorecardFormat";
import { CellSpec, PdfDoc } from "./pdfDoc";
import { heading, HOUSE_WIDTH, HouseOptions, houseDoc, houseTable, note, paragraph, table, titleBlock, withMeaning } from "./houseStyle";

/** The Retention tab as a PDF: Day 1, 7 and 30 retention, overall and by when members registered. */

interface RetentionDay {
  eligible: number;
  retained: number;
  rate: number | null;
}

export interface RetentionCohort {
  cohort: string;
  members: number;
  day1: RetentionDay;
  day7: RetentionDay;
  day30: RetentionDay;
}

export interface RetentionPdfData {
  range: DayWindow;
  timezone: string;
  region: string | null;
  cohortSize: Granularity;
  overall: RetentionCohort;
  cohorts: RetentionCohort[];
}

const DAYS = [
  { key: "day1", label: "Day 1", meaning: "Opened the app in the 24 hours starting one day after registering." },
  { key: "day7", label: "Day 7", meaning: "Opened the app in the 24 hours starting seven days after registering." },
  { key: "day30", label: "Day 30", meaning: "Opened the app in the 24 hours starting thirty days after registering." },
] as const;

const share = (day: RetentionDay) => (day.eligible ? `${formatPercent(day.rate)}  (${formatCount(day.retained)}/${formatCount(day.eligible)})` : "—");

export function buildRetentionPdf(data: RetentionPdfData, options: HouseOptions): PdfDoc {
  const blocks = titleBlock(
    "Retention",
    [`Members who registered ${formatDays(data.range)}`, `Region: ${data.region ?? "Everywhere"} · Days in ${data.timezone}`],
    options.preparedBy,
  );
  if (options.includeMeanings) {
    blocks.push(
      paragraph(
        "Of the members who registered in these dates, how many came back on their first day after registering, their seventh and their thirtieth. A member only counts once that day is over for them, so recent signups are left out of both sides of the rate rather than counted as gone. Active means they opened or used the app. Banned accounts are left out; deleted ones count, because a member who left is exactly who retention has to count.",
        12,
      ),
    );
  }

  blocks.push(heading("Members who came back"));
  blocks.push(
    table(
      houseTable(
        [150, HOUSE_WIDTH - 150 - 160, 160],
        [{ text: "Retention" }, { text: "Share who came back" }, { text: "Members counted", align: "right" }],
        DAYS.flatMap(({ key, label, meaning }) => {
          const day = data.overall[key];
          const cells: CellSpec[] = [
            { text: label, style: { style: "bold" } },
            { text: day.eligible ? formatPercent(day.rate) : "Not over yet for anyone", style: { style: "bold" } },
            { text: day.eligible ? `${formatCount(day.retained)} of ${formatCount(day.eligible)}` : "", align: "right" },
          ];
          return withMeaning(cells, options.includeMeanings ? meaning : null);
        }),
      ),
    ),
  );

  const by = data.cohortSize === "MONTH" ? "month" : "week";
  blocks.push(heading(`By the ${by} members registered`));
  blocks.push(
    note(
      `Each row is the members who registered that ${by}: the share who came back, then how many of how many. A dash means the day is not over yet for anyone in that group. Comparing rows shows whether newer members are coming back more or less than earlier ones.`,
    ),
  );
  const width = (HOUSE_WIDTH - 120 - 70) / 3;
  blocks.push(
    table(
      houseTable(
        [120, 70, width, width, width],
        [{ text: "Registered" }, { text: "Members", align: "right" }, ...DAYS.map(({ label }) => ({ text: label, align: "right" as const }))],
        [...data.cohorts].reverse().map((cohort) => [
          { text: formatPeriod(cohort.cohort, data.cohortSize) },
          { text: formatCount(cohort.members), align: "right" },
          ...DAYS.map(({ key }) => ({ text: share(cohort[key]), align: "right" as const })),
        ]),
      ),
    ),
  );
  if (data.cohorts.length === 0) blocks.push(note("No one registered in these dates."));

  return houseDoc(`ILBP Retention ${data.range.from} to ${data.range.to}.pdf`, blocks, "ILBP Retention");
}
